import { useT } from "../../../../shared/i18n/i18n";
import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, CircleSlash, Plus, Tag, Trash2 } from "lucide-react";
import type { WorkObjectTypeCategory } from "../../../../entities/work-object-type/model/use-work-object-types";
import { EditableText } from "@/shared/ui/editable-text";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type WorkObjectTypeCategoryPickerProps = {
  categories: WorkObjectTypeCategory[];
  disabled?: boolean;
  onArchive: (categoryId: string) => Promise<void>;
  onCreate: (name: string) => Promise<WorkObjectTypeCategory>;
  onRename: (input: { id: string; name: string }) => Promise<WorkObjectTypeCategory>;
  onValueChange: (categoryId: string | null) => void;
  value: string | null;
};

export function WorkObjectTypeCategoryPicker({
  categories,
  disabled,
  onArchive,
  onCreate,
  onRename,
  onValueChange,
  value,
}: WorkObjectTypeCategoryPickerProps) {
  const t = useT();
  const [draftName, setDraftName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => categories.find((category) => category.id === value) ?? null, [categories, value]);

  async function createCategory() {
    const name = draftName.trim();
    if (name.length < 2) {
      return;
    }

    setError(null);
    try {
      const created = await onCreate(name);
      onValueChange(created.id);
      setDraftName("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create category.");
    }
  }

  async function renameCategory(category: WorkObjectTypeCategory, name: string) {
    if (name.trim() === category.name) {
      return;
    }

    setError(null);
    try {
      await onRename({ id: category.id, name });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not rename category.");
    }
  }

  async function archiveCategory(category: WorkObjectTypeCategory) {
    setError(null);
    try {
      await onArchive(category.id);
      if (category.id === value) {
        onValueChange(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive category.");
    }
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        disabled={disabled}
        render={
          <Button className="h-8 w-full justify-between bg-muted/20 px-2 shadow-none hover:bg-muted/35" type="button" variant="ghost">
            <span className="flex min-w-0 items-center gap-2">
              {selected ? <Tag className="size-4 text-muted-foreground" /> : <CircleSlash className="size-4 text-muted-foreground" />}
              <span className="truncate">{selected?.name ?? t("itemTypes.noCategory")}</span>
            </span>
            <ChevronsUpDown className="size-3.5 text-muted-foreground" />
          </Button>
        }
      />
      <PopoverContent align="start" className="w-[min(24rem,calc(100vw-2rem))] gap-2 p-2">
        <PopoverHeader className="px-1">
          <PopoverTitle>{t("itemTypes.typeCategory")}</PopoverTitle>
        </PopoverHeader>

        <div className="flex max-h-64 flex-col gap-1 overflow-y-auto pr-1">
          <button
            className={cn(
              "flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors hover:bg-accent/70",
              value === null && "bg-accent/60",
            )}
            onClick={() => onValueChange(null)}
            type="button"
          >
            <span className="flex size-5 shrink-0 items-center justify-center rounded text-primary">
              {value === null ? <Check className="size-4" /> : null}
            </span>
            <CircleSlash className="size-4 text-muted-foreground" />
            <span className="font-medium">{t("itemTypes.noCategory")}</span>
          </button>
          {categories.map((category) => (
            <div
              className={cn(
                "group flex min-h-9 items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors hover:bg-accent/70",
                category.id === value && "bg-accent/60",
              )}
              key={category.id}
            >
              <button
                aria-label={t("a11y.pickCategory", { name: category.name })}
                className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded text-primary"
                onClick={() => onValueChange(category.id)}
                type="button"
              >
                {category.id === value ? <Check className="size-4" /> : null}
              </button>
              <EditableText
                ariaLabel={t("a11y.renameCategory", { name: category.name })}
                className="min-w-0 flex-1"
                inputClassName="text-sm font-medium leading-5"
                onChange={(name) => void renameCategory(category, name)}
                placeholder={t("itemTypes.categoryName")}
                previewClassName="text-sm font-medium leading-5"
                value={category.name}
              />
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      className="size-7 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                      onClick={() => void archiveCategory(category)}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  }
                />
                <TooltipContent>{t("itemTypes.archiveCategory")}</TooltipContent>
              </Tooltip>
            </div>
          ))}
        </div>

        <form
          className="flex items-center gap-2 border-t border-border/60 pt-2"
          onSubmit={(event) => {
            event.preventDefault();
            void createCategory();
          }}
        >
          <Input
            aria-label={t("itemTypes.newCategoryName")}
            className="h-8 flex-1"
            onChange={(event) => setDraftName(event.target.value)}
            placeholder={t("itemTypes.newCategory")}
            value={draftName}
          />
          <Button disabled={draftName.trim().length < 2} size="icon" type="submit" variant="ghost">
            <Plus className="size-4" />
          </Button>
        </form>
        {error ? <p className="px-1 text-xs text-destructive">{error}</p> : null}
      </PopoverContent>
    </Popover>
  );
}
